import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { AudioStyles } from 'styles/style';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import {
  FaPlay,
  FaPause,
  FaForward,
  FaBackward,
  FaVolumeUp,
  FaVolumeMute,
} from 'react-icons/fa';
import icon from '../../images/lra.svg';

const EXTENSIONS = ["mp3", "wav", "m4a", "ogg"];

const AudioContent = ({ audio, title }) => {
  const [song] = useState(audio);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [currentLineIndex, setCurrentLineIndex] = useState(-1);

  const audioRef = useRef(null);
  const lyricsContainerRef = useRef(null);
  const lineRefs = useRef([]);

  const parsedLyrics = useMemo(() => song?.lyric_sync || [], [song]);

  const [src, setSrc] = useState(null);
  const indexRef = useRef(0);

  const tryNext = useCallback(() => {
    if (!song.audio) return;
    if (indexRef.current >= EXTENSIONS.length) return;

    const testSrc = `https://zegleedmcpswaxynxnta.supabase.co/storage/v1/object/public/assets/${song.audio}.${EXTENSIONS[indexRef.current]}`;
    setSrc(testSrc);
    indexRef.current++;
  }, [song.audio]);

  useEffect(() => {
    if (!song.audio) return;
    indexRef.current = 0;
    tryNext();
  }, [song.audio, tryNext]);


  const findCurrentLine = useCallback((time) => {
    for (let i = parsedLyrics.length - 1; i >= 0; i--) {
      if (time >= parsedLyrics[i].time) {
        return i;
      }
    }
    return -1;
  }, [parsedLyrics]);

  useEffect(() => {
    const newIndex = findCurrentLine(progress);

    if (newIndex !== currentLineIndex) {
      setCurrentLineIndex(newIndex);

      const el = lineRefs.current[newIndex];
      if (el && lyricsContainerRef.current) {
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [progress, findCurrentLine, currentLineIndex]);


  const handleLyricClick = (time) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setProgress(time);
    }
  };

  const skipTime = (seconds) => {
    if (audioRef.current) {
      const player = audioRef.current;
      player.currentTime = Math.min(
        Math.max(player.currentTime + seconds, 0),
        player.duration || player.currentTime
      );
    }
  };

  const skipBackTime = (seconds) => {
    if (audioRef.current) {
      const player = audioRef.current;
      player.currentTime = Math.min(
        Math.max(player.currentTime - seconds, 0),
        player.duration || player.currentTime
      );
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    setProgress(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    const value = e.target.value;
    audioRef.current.currentTime = value;
    setProgress(value);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <AudioStyles.Section>
      <AudioStyles.PlayerCard>
        {song?.lyrics && (
          <>
            <AudioStyles.SongInfo>
              <AudioStyles.AlbumCover src={icon} alt="album" />
              <AudioStyles.SongText>
                <AudioStyles.SongTitle>
                  {song.title || title}
                </AudioStyles.SongTitle>
                <AudioStyles.SongArtist>Audio Content</AudioStyles.SongArtist>
              </AudioStyles.SongText>
            </AudioStyles.SongInfo>
          </>
        )}

        <AudioStyles.AudioControllerCont>
          <AudioStyles.ProgressWrapper>
            <AudioStyles.TimeRow>
              <span>
                {new Date(progress * 1000).toISOString().substr(14, 5)}
              </span>
              <span>
                {new Date(duration * 1000).toISOString().substr(14, 5)}
              </span>
            </AudioStyles.TimeRow>
            <AudioStyles.ProgressBar
              type="range"
              min="0"
              max={duration || 0}
              value={progress}
              onChange={handleSeek}
            />
          </AudioStyles.ProgressWrapper>

          <AudioStyles.SongControls>
            <AudioStyles.SongControlButton onClick={() => skipBackTime(10)}>
              <FaBackward />
            </AudioStyles.SongControlButton>
            <AudioStyles.PlayButton onClick={togglePlay}>
              {isPlaying ? <FaPause /> : <FaPlay />}
            </AudioStyles.PlayButton>
            <AudioStyles.SongControlButton onClick={() => skipTime(10)}>
              <FaForward />
            </AudioStyles.SongControlButton>
            <AudioStyles.SongControlButton onClick={toggleMute}>
              {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
            </AudioStyles.SongControlButton>
          </AudioStyles.SongControls>
        </AudioStyles.AudioControllerCont>

        {parsedLyrics.length > 0 ? (
          <AudioStyles.LyricsContainer ref={lyricsContainerRef}>
            {parsedLyrics.map((line, index) => (
              <AudioStyles.LyricLine
                key={index}
                ref={(el) => (lineRefs.current[index] = el)}
                isActive={index === currentLineIndex}
                onClick={() => handleLyricClick(line.time)}
              >
                {line.text}
              </AudioStyles.LyricLine>
            ))}
          </AudioStyles.LyricsContainer>
        ) : (
          <>
            {song.lyrics && (
              <AudioStyles.LyricsBox>
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                  {song.lyrics}
                </ReactMarkdown>
              </AudioStyles.LyricsBox>
            )}
          </>
        )}

        {/* Audio Element */}
        {song.audio && (
          <audio
            ref={audioRef}
            src={src}
            onError={() => tryNext()}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
          />
        )}
      </AudioStyles.PlayerCard>
    </AudioStyles.Section>
  );
};

export default AudioContent;
